import { FieldSelect } from '@dixa/lib-shared-habla';
import {
  CategoryFieldsFragment,
  useCategoryDropdownQuery,
} from 'lib-knowledge-management-common/graphql';
import {
  Category,
  CategoryRef,
  CollectionRef,
  ParentRef,
} from 'lib-knowledge-management-common/models/types';
import { getCategoryDropdownItems } from 'lib-knowledge-management-common/utils/categoryDropdownItems';
import { useState } from 'react';

export type Value =
  | (CategoryRef & {
      titles: CategoryFieldsFragment['titles'];
    })
  | CollectionRef;

type CategoryTreeInputProps = {
  collectionId: string;
  value: Value | undefined | null;

  onChange: (parentRef: ParentRef) => void;
  onToggle?: (opened: boolean) => void;

  label?: string;
  placeholder?: string;
  searchable?: boolean;
  required?: boolean;

  disabled?: boolean;
  touched?: boolean;

  className?: string;

  /** Prevent articles being added to the collection. */
  withCollectionOption: boolean;

  /**
   * Prevent a category from being moved into itself or one of it's children.
   * The current category will be disabled and it's children filtered out.
   */
  currentCategoryId: string | undefined;

  disabledCategoryIds?: Array<string>;
};

/**
 * Component to Render a tree of categories relating to the currently selected
 * item
 */
const CategoryDropdown = ({
  collectionId,
  value,
  onChange,
  onToggle,
  withCollectionOption,
  currentCategoryId,
  disabledCategoryIds = [],
  required,
  touched = false,
  placeholder,
  label,
  className,
  disabled,
}: CategoryTreeInputProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, loading } = useCategoryDropdownQuery({
    variables: { collectionId },
    skip: !isOpen,
  });

  const handleChange = (id: string | undefined) => {
    if (!collectionId || !id) return;
    id === collectionId
      ? onChange({
          __typename: 'Collection',
          id: collectionId,
        })
      : onChange({
          __typename: 'Category',
          id,
        });
  };

  const categories = (data?.collection?.categories ||
    // Show existing category if passed in.
    (value?.__typename === 'Category'
      ? [value].filter(Boolean)
      : [])) as Category[];

  const { allDisabledCategoryIds, listItems } = getCategoryDropdownItems({
    categories,
    withCollectionOption,
    currentCategoryId,
    disabledCategoryIds,
    collectionId,
  });

  return (
    <FieldSelect
      disabled={disabled || loading}
      searchable
      className={className}
      label={label}
      noLabel={!label}
      required={required}
      onToggle={(newOpen) => {
        setIsOpen(newOpen);
        onToggle?.(newOpen);
      }}
      input={{
        name: 'categoryDropdown',
        value: value?.id ? [value.id] : [],
        onChange: handleChange,
      }}
      meta={{
        error: required && !value?.id ? 'Category is required' : undefined,
        touched,
      }}
      disabledItems={allDisabledCategoryIds}
      items={loading ? [] : listItems}
      placeholder={loading ? 'Loading Categories...' : placeholder}
    />
  );
};

export default CategoryDropdown;
